// @ts-strict-ignore
import * as React from 'react';

import { NeedlAutocompletePopper } from '@/components/NeedlAutocomplete';
import { NEEDL_BOOKMARK_TAG } from '@/components/UserTags/common';
import { useDocumentTags } from '@/components/UserTags/hooks/useDocumentTags';
import { useMutateTags } from '@/components/UserTags/hooks/useMutateTags';
import { useUserTags } from '@/components/UserTags/hooks/useUserTags';
import type {
  OnSuccessProps,
  Option,
} from '@/components/UserTags/TagAutocomplete/types';
import { isCreatable } from '@/components/UserTags/TagAutocomplete/utils';
import { useConstructSearchQueryParams } from '@/hooks/useConstructSearchQueryParams';
import { sortSelectedSourcesFromTwoArrays } from '@/utils/sort';

const TagSelectorContext = React.createContext<TagSelectorContextType | null>(
  null
);

export const useTagSelectorContext = () => {
  const context = React.useContext(TagSelectorContext);
  if (!context) {
    throw new Error(
      'useTagSelectorContext must be used within a TagSelectorProvider'
    );
  }
  return context;
};

export const TagSelectorProvider: React.FunctionComponent<
  TagSelectorProviderProps
> = ({ children, deeplink, disabled, onOpen, onClose, onSuccess }) => {
  const { board_id, channel_id } = useConstructSearchQueryParams();
  const { data: userTagsData, isLoading: userTagsLoading } = useUserTags();
  const { data: documentTagsData, isLoading: documentTagsLoading } =
    useDocumentTags(deeplink);
  const mutate = useMutateTags();

  const [disabledCheckBox, setDisabledCheckBox] = React.useState(false);
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const { anchorEl, handleClose, handleOpen, open } =
    NeedlAutocompletePopper.usePopperState();

  const id = open ? 'tags-label' : undefined;

  const isBookmarked = documentTagsData?.tags?.includes(NEEDL_BOOKMARK_TAG);

  const userTags = userTagsData?.userTags;
  const documentTags = React.useMemo(
    () => documentTagsData?.userTags,
    [documentTagsData]
  );

  const userTagsSet = React.useMemo(() => new Set(userTags), [userTags]);

  const selectionRef = React.useRef<string | null>(null);

  const isDisabled = disabled || documentTagsLoading || userTagsLoading;

  const options = sortSelectedSourcesFromTwoArrays(
    userTags ?? [],
    documentTags ?? []
  );

  React.useEffect(() => {
    // sync initial state with the document tags state
    // fetched from the server
    setSelectedTags(documentTags ?? []);
  }, [documentTags]);

  const onChange = React.useCallback(
    (e: React.ChangeEvent<unknown>, values: Option[]) => {
      const newTag = values.find(isCreatable);
      if (newTag) {
        setDisabledCheckBox(true);
        mutate.mutate(
          { tag: newTag.inputValue },
          {
            onSettled: () => {
              setDisabledCheckBox(false);
              selectionRef.current = newTag.inputValue;
            },
          }
        );
        return;
      }
      setSelectedTags(values as string[]);
    },
    [mutate]
  );

  const openSelector = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onOpen?.(event);
      handleOpen(event);
    },
    [handleOpen, onOpen]
  );

  const closeSelector = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClose?.(event);
      handleClose(event, 'blur');
      setSelectedTags(documentTags ?? []);
    },
    [documentTags, handleClose, onClose]
  );

  const primaryButtonHandler = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const tags = [...(selectedTags || [])];

      onSuccess?.({ tags, board_id, channel_id, isBookmarked });
      closeSelector(event);
    },
    [board_id, channel_id, closeSelector, isBookmarked, onSuccess, selectedTags]
  );

  if (selectionRef.current !== null && !mutate.isLoading) {
    const newTag = selectionRef.current;
    selectionRef.current = null;

    setSelectedTags((prev) => [...prev, newTag]);
  }

  return (
    <TagSelectorContext.Provider
      value={React.useMemo(
        () => ({
          disabled: isDisabled,
          documentTagsLoading,
          userTagsLoading,
          id,
          anchorEl,
          open,
          disableAutocomplete: disabledCheckBox,
          openSelector,
          userTagsSet,
          onChange,
          closeSelector,
          options,
          primaryButtonHandler,
          selectedTags,
        }),
        [
          isDisabled,
          documentTagsLoading,
          userTagsLoading,
          id,
          anchorEl,
          open,
          disabledCheckBox,
          openSelector,
          userTagsSet,
          onChange,
          closeSelector,
          options,
          primaryButtonHandler,
          selectedTags,
        ]
      )}
    >
      {children}
    </TagSelectorContext.Provider>
  );
};

type TagSelectorContextType = {
  disabled: boolean;
  openSelector: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  documentTagsLoading: boolean;
  userTagsLoading: boolean;
  id: string | undefined;
  anchorEl: HTMLButtonElement | null;
  open: boolean;
  disableAutocomplete: boolean;
  userTagsSet: Set<string>;
  closeSelector: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  onChange: (event: React.ChangeEvent<unknown>, value: Option[]) => void;
  options: string[];
  primaryButtonHandler: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  selectedTags: string[];
};

type TagSelectorProviderProps = {
  children: React.ReactNode;
  deeplink: string;
  disabled?: boolean;
  onClose?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onOpen?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSuccess?: (props: OnSuccessProps) => void;
};
